import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { UrlService } from '@core/services/url/url.service';

import * as vsModel from '@core/models/version-share.model';
import { FeedbackStatusColorValues as LegacyFeedbackStatusColorValues, FeedbackStatusCodeEnum as LegacyFeedbackStatusCodeEnum } from '@core/models/version-feedback';
import { Version } from '@core/objects/version';

@Injectable({
    providedIn: 'root'
})
/**
 * Service to use utils for new bubbles object set according to 
 * new feedback types and new features
 * <diego@twonas.com> at 8/12/2023
 */
export class VersionSharesService {

    protected readonly MAX_VERSIONS_PER_CALL = 100;

    constructor(
        protected url: UrlService, 
        protected http: HttpClient,
    ) {}

    /**
     * 
     * @param share an object for each user share in the same version
     * with the status according the type of feedback in the bubble.
     * @returns string with the color to display
     */
    public getVersionShareBubbleColor(share: vsModel.VersionShare) : string {
        // same "wrong data" yellow the legacy feedback bubbles used when the status was missing
        if (share?.status == null)
            return LegacyFeedbackStatusColorValues[LegacyFeedbackStatusCodeEnum.WRONG_CODE];
        switch (share.type) {
            case vsModel.VersionShareType.UPLOAD_VERSION_TYPE:
                return vsModel.UploadVersionStatusColorValues[share.status.status_code];
                break;
            case vsModel.VersionShareType.FEEDBACK_TYPE:
                return vsModel.FeedbackStatusColorValues[share.status.status_code];
                break;
            case vsModel.VersionShareType.APPROVAL_TYPE:
                return vsModel.ApprovalStatusColorValues[share.status.status_code];
                break;
            default:
                return  vsModel.FeedbackStatusColorValues[0];
                break;
        }
    }

    /**
     * oct-2026: bubble tooltip (its letter is the first character). A pending New Artwork Version request
     * is silver like an open Comments request, so the tooltip tells them apart.
     */
    public getVersionShareBubbleTitle(share: vsModel.VersionShare) : string {
        const name = share?.entry?.user?.name ?? '';
        if (share?.type === vsModel.VersionShareType.UPLOAD_VERSION_TYPE && vsModel.isVersionShareOpen(share))
            return name + ' (new version)';
        return name;
    }

    // Load bubbles for a version without store (used in logbook)
    getVersionShares$(version: Version) {
        return this.http
        .get(this.url.build("/versions/" + version.id + "/shares"));
    }

    /**
     * oct-2026: bubbles for several versions in one call, replaces the legacy
     * `GET /p/feedback/versions/feedbacks/{ids}`.
     * The backend answers 400 above 100 ids, so longer lists are split and requested in parallel.
     * `[GET] /web/app/versions/shares?versions={id},{id},...`
     */
    getVersionsShares$(versions: Array<Pick<Version, 'id'>>): Observable<vsModel.VersionSharesByVersion[]> {
        const ids = versions.map((v: Pick<Version, 'id'>) => v.id);
        if (ids.length == 0)
            return of([]);
        const chunks: Array<Array<number>> = [];
        for (let i = 0; i < ids.length; i += this.MAX_VERSIONS_PER_CALL)
            chunks.push(ids.slice(i, i + this.MAX_VERSIONS_PER_CALL));
        return forkJoin(chunks.map((chunk: Array<number>) =>
            this.http.get<vsModel.VersionSharesByVersion[]>(this.url.build("/versions/shares"), { params: new HttpParams().set('versions', chunk.join(',')) })
        )).pipe(map((results: vsModel.VersionSharesByVersion[][]) => results.flat()));
    }

    /**
     * oct-2026: bubbles of one or several versions as a flat list, replaces the legacy
     * `GET /p/feedback/versions/feedbacks/{ids}` (`VersionFeedback[]`) in every list page.
     * One version → `GET /versions/{version_id}/shares`; several → `GET /versions/shares?versions=...`.
     * Each entry carries its `version.id`, so callers keep filtering by version as before.
     */
    getVersionShareItems$(versions: Array<Pick<Version, 'id'>>): Observable<vsModel.VersionShareItem[]> {
        if (versions == null || versions.length == 0)
            return of([]);
        const byVersion$: Observable<vsModel.VersionSharesByVersion[]> = versions.length == 1
            ? this.http.get<vsModel.VersionShare[]>(this.url.build("/versions/" + versions[0].id + "/shares"))
                .pipe(map((shares: vsModel.VersionShare[]) => [{ version: { id: versions[0].id }, shares: shares ?? [] }]))
            : this.getVersionsShares$(versions);
        return byVersion$.pipe(map((byVersion: vsModel.VersionSharesByVersion[]) => this.toShareItems(byVersion)));
    }

    protected toShareItems(byVersion: vsModel.VersionSharesByVersion[]): vsModel.VersionShareItem[] {
        return (byVersion ?? []).flatMap((item: vsModel.VersionSharesByVersion) =>
            (item.shares ?? [])
                // The list bubbles only show Approval/Comments requests, as the legacy endpoint did: the Approvals
                // section filters (feedbacks_status) cannot filter New Artwork Version requests, so lists must not
                // show bubbles they cannot filter (decided 2026-10-27, after showing every type for a few days).
                // Types available, if that changes:
                // - vsModel.VersionShareType.APPROVAL_TYPE       ("approval")
                // - vsModel.VersionShareType.FEEDBACK_TYPE       ("feedback", comments only)
                // - vsModel.VersionShareType.UPLOAD_VERSION_TYPE ("upload-version", New Artwork Version request)
                // To show all of them, drop the filter; getVersionShareBubbleColor() handles the colours of every type.
                .filter((share: vsModel.VersionShare) => vsModel.isFeedbackVersionShare(share))
                .map((share: vsModel.VersionShare) => ({ ...share, version: item.version }))
        );
    }
}
